/* START OF COMPILED CODE */

import Phaser from 'phaser';
import PlayerCharacter from '../components/player/PlayerCharacter';
import { initInventoryManager, getInventoryManager } from "../managers/InventoryManager";
import { itemManager } from "../managers/ItemManager";
import { careManager } from '../managers/CareManager';
import { DialogueWindow } from '../components/ui/DialoguePanel';

export default class RiverScene extends Phaser.Scene {
    constructor() {
        super({ key: 'River' });
        this.debugMode = false; // Debug mode flag
        this.isTransitioning = false; // Scene transition flag
        this.isBarricaded = true;

        // Fishing system
        this.fishing = {
            isFishing: false,
            rod: null,
            bobber: null,
            ripples: []
        };
        

        
        // Minigame properties
        this.minigame = null;
        this.minigameFish = null;
        this.minigameOverlap = null;
        this.minigameKeys = null;
        this.catchComplete = 0;
        this.overlapTimer = null;
        this.fishMovementTimer = null;

        // Add sound management
        this.sounds = {
            splash: null,
            cast: null,
            reeling: null,
            caught1: null,
            caught2: null,
            caught3: null,
            caught4: null,
            lost: null,
            catchSplash: null,
            river_ambiance: null
        };

        // Item criteria for fishing in this scene
        this.fishingCriteria = {
            type: 'Consumable',
            subtypePrefix: 'Fish/Freshwater'
        };
    }

    preload() {
        // All assets are pre-loaded by LoadingScene
        // Only load items if needed
        //itemManager.preloadAssets(this);


    }



    async create(data = {}) {
        initInventoryManager(this);
        this.isTransitioning = false; // Scene transition flag

        // Create player animations
        PlayerCharacter.createAnimations(this);

        this.anims.create({
            key: 'rod',
            frames: this.anims.generateFrameNumbers('fishingRod', { start: 0, end: 4 }),
            frameRate: 8,
            repeat: 0
        });
    
        // Create ripple animation without duration
        this.anims.create({
            key: 'ripple',
            frames: this.anims.generateFrameNumbers('ripple', { start: 0, end: 5 }),
            frameRate: 8,
            repeat: -1
        });

        this.anims.create({
            key: 'bobberFloating',
            frames: this.anims.generateFrameNumbers('bobberFloating', { start: 0, end: 3 }),
            frameRate: 4,
            repeat: -1
        });

        this.anims.create({
            key: 'bobberBite',
            frames: this.anims.generateFrameNumbers('bobberBite', { start: 0, end: 3 }),
            frameRate: 8,
            repeat: -1
        });
        
        // Setup camera with larger world bounds for 512x384 map (like Farm scene)
        this.cameras.main.setViewport(0, 0, 256, 192);
        this.cameras.main.setBounds(0, 0, 512, 384);
        this.cameras.main.setZoom(1);
        
   
        // Load background with depth (updated to new larger size)
        this.add.image(256, 192, 'river').setDepth(0);
        
        // Add new bridge layer (same size as scene, overlaid centrally)
        this.add.image(256, 192, 'river_new_bridge_locked').setDepth(1);
        //this.add.image(256, 192, 'river_new_bridge_unlocked').setDepth(1);

        // Add barricade image (adjust position as needed)
        this.add.image(496, 58, 'barricade').setDepth(2);

        // Get user's preferred color and setup player
        const playerColor = await PlayerCharacter.getUserColor(this);
        
        // Define spawn points for different entry locations (updated for larger world)
        // Add new spawn points here when connecting to other scenes
        // Format: sceneName: { x: positionX, y: positionY }
        const spawnPoints = {
            default: { x: 50, y: 50 }, // Default spawn (updated for larger world)
            fromFarm: { x: 50, y: 96 }, // Spawn near farm transition (left side, centered vertically)
            fromBeach: { x: 256, y: 350 }, // Spawn near beach transition (bottom, centered horizontally)
            fromMainScene: { x: 256, y: 50 }, // Spawn near main scene transition (top, centered)
        };
        
        // Determine spawn position based on data passed from previous scene
        const spawnKey = data.spawnPoint || 'default';
        const spawnPosition = spawnPoints[spawnKey] || spawnPoints.default;
        
        //console.log(`River scene spawning player at: ${spawnKey} (${spawnPosition.x}, ${spawnPosition.y})`);
        
        // Setup player with spawn position
        this.player = new PlayerCharacter(this, spawnPosition.x, spawnPosition.y, playerColor);
        this.player.setDepth(5); // Above all background layers
        this.player.playColorAnimation("idle2", true);
        
        // Camera follows player (like Farm scene)
        this.cameras.main.startFollow(this.player);
        
        // Initialize mood dialogue tracking for movement dismissal
        this.activeMoodDialogue = null;
        this.lastPlayerPosition = { x: this.player.x, y: this.player.y };

        // Setup game systems in order
        this.setupFishing();
        this.setupFishingZones(); 
        this.setupAudio();
        this.setupCollision();
        this.setupInputHandlers();
        
        // Setup debug mode
        //this.setupDebugMode();
        
        // Initialize collision visuals
        //this.updateCollisionVisuals();
        
        // Create ripples after fishing zones are ready
        this.createRipples();
    }

    setupFishing() {
        // Create sprite with specific frame and scaling
        this.fishing.rod = this.add.sprite(0, 0, 'fishingRod', 0);
        this.fishing.rod.setScale(0.5);
        this.fishing.rod.setVisible(false);
        
        // Create fishing line
        this.fishing.line = this.add.graphics();
    }

    setupFishingZones() {
        // Fishing zones are now the same as collision areas (water/river areas)
        // Collision bodies represent water that blocks player movement = VALID fishing zones
        // No separate fishing zone rectangles needed
        //console.log('Fishing zones configured to use collision areas (water/river zones)');
    }

    setupAudio() {
        // Stop any existing ambiance
        if (this.sound.get('river_ambiance')) {
            this.sound.get('river_ambiance').stop();
        }
        
        // Initialize all sound instances once
        this.sounds.river_ambiance = this.sound.add('river_ambiance');
        this.sounds.splash = this.sound.add('splash', { volume: 0.4 });
        this.sounds.cast = this.sound.add('cast', { volume: 0.4 });
        this.sounds.reeling = this.sound.add('reeling', { volume: 0.4 });
        this.sounds.reeling.addMarker({
            name: 'middle_section',
            start: 1,
            duration: 1.5,
            config: { loop: true }
        });
        this.sounds.reelingStart = this.sound.add('reelingStart', { volume: 0.4 });
        this.sounds.catchSplash = this.sound.add("catchSplash", { volume: 0.4 });
        this.sounds.caught1 = this.sound.add('caught1', { volume: 0.4 });
        this.sounds.caught2 = this.sound.add('caught2', { volume: 0.4 });
        this.sounds.caught3 = this.sound.add('caught3', { volume: 0.4 });
        this.sounds.caught4 = this.sound.add('caught4', { volume: 0.4 });
        this.sounds.lost = this.sound.add('lost', { volume: 0.4 });

        // Start ambiance
        this.sounds.river_ambiance.loop = true;
        this.sounds.river_ambiance.play();
    }

    setupCollision() {
        // Set world bounds for the larger map (updated from 256x192 to 512x384)
        this.physics.world.setBounds(0, 0, 512, 384);
        this.player.body.setCollideWorldBounds(true);
        
        // Create simple rectangular collision areas instead of pixel-perfect collision
        this.collisionBodies = this.physics.add.staticGroup();
        
        // Define collision rectangles for walls and boundaries
        // To adjust: Change x, y (center position), width, height values
        // Press 'Z' in-game to toggle debug mode and see collision rectangles
        // NOTE: These collision areas will need to be updated for the new 512x384 layout
        // The user will use debug mode to reposition these later
        const collisionAreas = [
            { x: 50, y: 268, width: 104, height: 232 },
            { x: 152, y: 309, width: 304, height: 150 },
            { x: 250, y: 235, width: 107, height: 297 },
            { x: 246, y: 16, width: 103, height: 32 },
            { x: 416, y: 171, width: 224, height: 104 },
            { x: 496, y: 58, width: 40, height: 113 }, // barricade collision
        ];
        
        // Create collision rectangles
        collisionAreas.forEach(area => {
            const rectangle = this.add.rectangle(area.x, area.y, area.width, area.height, 0xFF0000, 0);
            this.physics.add.existing(rectangle, true); // true makes it static
            this.collisionBodies.add(rectangle);
        });
        
        // Set up collision between player and collision bodies
        this.physics.add.collider(this.player, this.collisionBodies);
        
        // Set up scene transition areas
        this.setupSceneTransitions();
    }

    setupInteractables() {
        // Create a separate group for scene transition areas
        this.interactableBodies = this.physics.add.staticGroup();

        const interactableAreas = [
            { x: 496, y: 58, width: 50, height: 113 }, // barricade
        ];

        interactableAreas.forEach(area => {
            const rectangle = this.add.rectangle(area.x, area.y, area.width, area.height, 0x00FF00, 0);
            this.physics.add.existing(rectangle, true); // true makes it static
            
            this.interactableBodies.add(rectangle);
        });

        this.physics.add.overlap(this.player, this.interactableBodies, (player, interactableArea) => {
            dialogue.setMultiDialogue([
                ["The way to the old castle is barricaded..?", "question"],
                ["I wonder what's on the other side..", "exclamation"],
            ]);
        });
    }
    
    setupSceneTransitions() {
        // Create a separate group for scene transition areas
        this.sceneTransitionBodies = this.physics.add.staticGroup();
        
        // Define scene transition rectangles
        // These use overlap detection (not collision) to trigger scene changes
        // To add more transitions: Add objects with x, y, width, height, targetScene
        // Use debug mode (Z key) to see green transition rectangles
        // Updated for new 512x384 world size
        const transitionAreas = [
            // Farm scene transition (left edge, updated for larger world)
            { x: 1, y: 72, width: 2, height: 40, targetScene: 'Farm' },
            // Add more transitions here as needed:
        ];
        
        // Create transition rectangles
        transitionAreas.forEach(area => {
            const rectangle = this.add.rectangle(area.x, area.y, area.width, area.height, 0x00FF00, 0);
            this.physics.add.existing(rectangle, true); // true makes it static
            
            // Store the target scene in the rectangle for easy access
            rectangle.targetScene = area.targetScene;
            
            this.sceneTransitionBodies.add(rectangle);
        });
        
        // Set up overlap detection (not collision) for scene transitions
        this.physics.add.overlap(this.player, this.sceneTransitionBodies, (player, transitionArea) => {
            this.handleSceneTransition(transitionArea.targetScene);
        });
    }
    
    handleSceneTransition(targetScene) {
        // Prevent multiple rapid transitions
        if (this.isTransitioning) return;
        
        this.isTransitioning = true;
        
        console.log(`Transitioning to ${targetScene} scene`);
        
        // Define spawn point data for the target scene based on current scene
        let sceneData = {};
        if (targetScene === 'Farm') {
            sceneData = { spawnPoint: 'fromRiver' };
        }
        // Add more spawn point mappings here as needed for other scenes
        
        // Optional: Add transition effect (fade out, etc.)
        this.cameras.main.fadeOut(300, 0, 0, 0);
        
        this.cameras.main.once('camerafadeoutcomplete', () => {
            // Clean up before scene change
            this.cleanup();
            
            // Change to the target scene with spawn data
            this.scene.start(targetScene, sceneData);
        });
    }
    
    setupDebugMode() {
        // Toggle debug mode with 'Z' key
        this.input.keyboard.on('keydown-Z', () => {
            this.debugMode = !this.debugMode;
            console.log('Debug mode:', this.debugMode ? 'ON' : 'OFF');
            this.updateCollisionVisuals(); // Update collision rectangle visibility
        });

        // H key to return to main scene
        this.input.keyboard.on('keydown-H', () => {
            if (!this.isTransitioning) {
                this.handleSceneTransition('MainScene');
            }
        });
        
        // State testing controls (for debugging the new animation system)
        this.input.keyboard.on('keydown-ONE', () => {
            this.player.modifyHunger(-20);
            console.log('Hunger decreased. Current state:', this.player.getState());
        });
        
        this.input.keyboard.on('keydown-TWO', () => {
            this.player.modifyEnergy(-20);
            console.log('Energy decreased. Current state:', this.player.getState());
        });
        
        this.input.keyboard.on('keydown-THREE', () => {
            this.player.modifyHappiness(-20);
            //console.log('Happiness decreased. Current state:', this.player.getState());
        });
        
        this.input.keyboard.on('keydown-FOUR', () => {
            this.player.setState({ hunger: 0, energy: 0 }); // Test critical state
        });
        
        this.input.keyboard.on('keydown-FIVE', () => {
            this.player.setState({ hunger: 100, energy: 100, happiness: 100 }); // Reset
        });
        
        this.input.keyboard.on('keydown-SIX', () => {
            // Toggle stat decay for testing
            if (this.player.statDecayTimer) {
                this.player.disableStatDecay();
            } else {
                this.player.enableStatDecay(1); // Fast decay for testing
            }
        });
        
        this.input.keyboard.on('keydown-SEVEN', () => {
            // Display current state information
            this.player.logStateInfo();
        });
    }
    
    updateCollisionVisuals() {
        // Update collision rectangle visibility based on debug mode
        if (this.collisionBodies && this.collisionBodies.children) {
            this.collisionBodies.children.entries.forEach(rectangle => {
                if (this.debugMode) {
                    rectangle.setStrokeStyle(2, 0xFF0000);
                    rectangle.setAlpha(0.3);
                } else {
                    rectangle.setStrokeStyle(0);
                    rectangle.setAlpha(0);
                }
            });
        }
        
        // Update scene transition rectangle visibility
        if (this.sceneTransitionBodies && this.sceneTransitionBodies.children) {
            this.sceneTransitionBodies.children.entries.forEach(rectangle => {
                if (this.debugMode) {
                    rectangle.setStrokeStyle(2, 0x00FF00);
                    rectangle.setAlpha(0.5);
                } else {
                    rectangle.setStrokeStyle(0);
                    rectangle.setAlpha(0);
                }
            });
        }
        
        // Fishing zones are now the same as collision areas (red rectangles)
        // No separate fishing zone visualization needed
    }

    setupInputHandlers() {
        // Pointer/click handler for fishing
        this.input.on('pointerdown', (pointer) => {
            // Don't cast fishing rod if clicking on a dialogue window
            if (this.isDialogueActive()) {
                return;
            }

            if (pointer.leftButtonDown()) {
                if (this.fishing.isFishing) {
                    if (this.fishing.hasBite) {
                        // Player tried to hook during bite window
                        if (!this.fishing.hooked) {
                            this.fishing.hooked = true;
                            this.startMinigame();
                        }
                    } else {
                        // If we're already fishing in a valid spot, cancel the fishing
                        this.endFishing();
                    }
                } else {
                    // Start fishing if we're not already fishing
                    this.handleFishing();
                }
            }
        });

        // Keep SPACE key handler as backup
        this.input.keyboard.on('keydown-SPACE', () => {
            if (this.fishing.isFishing) {
                if (this.fishing.hasBite ) {
                    // Player tried to hook during bite window
                    if(!this.fishing.hooked) {
                        this.fishing.hooked = true;
                        this.startMinigame();
                    }
                } else {
                    // If we're already fishing in a valid spot, cancel the fishing
                    if (this.fishing.isFishing) {
                        this.endFishing();
                    }
                }
            } else {
                // Start fishing if we're not already fishing
                this.handleFishing();
            }
        });
    }

    isDialogueActive() {
        // Check if any DialogueWindow exists in the scene
        return this.children.list.some(child => child instanceof DialogueWindow);
    }

    handleFishing() {
        if (this.fishing.isFishing) return;
        
        this.fishing.isFishing = true;
        this.player.setMovementLocked(true);
        
        const rodOffsetX = this.player.flipX ? -20 : 20;
        this.fishing.rod.setPosition(this.player.x + rodOffsetX, this.player.y);
        this.fishing.rod.setFlipX(this.player.flipX);
        this.fishing.rod.setVisible(true);
        this.fishing.rod.setFrame(0);
        
        // Remove any existing animation complete listeners
        this.fishing.rod.removeListener('animationcomplete');
        
        if (this.anims.exists('rod')) {
            // Add new listener and play sounds
            this.fishing.rod.once('animationcomplete', this.handleCastComplete, this);
            this.sounds.cast.play();
            this.fishing.rod.anims.play('rod');
        } else {
            console.error('Rod animation not found');
            this.handleCastComplete();
        }
    }

    handleBite() {
        if (!this.fishing.isFishing) return;
        
        this.fishing.hasBite = true;
        
        // Change bobber animation
        this.fishing.bobber.play('bobberBite');
        
        // Play splash sound
        this.sounds.splash.play();
        
        // Set up hook window timer (2.5 seconds)
        this.fishing.hookTimer = this.time.delayedCall(2500, () => {
            if (!this.fishing.hooked) {  // If still in bite state (player hasn't hooked)
                this.endFishing();  // Fish got away
            }
        }, [], this);
    }

    handleCastComplete() {
        const castDistance = Phaser.Math.Between(35, 70);
        let castX = this.player.x;
        let castY = this.player.y;
        
        const direction = this.player.getLastDirection();
        switch(direction) {
            case 'left':
                castX -= castDistance;
                break;
            case 'right':
                castX += castDistance;
                break;
            case 'up':
                castY -= castDistance;
                break;
            case 'down':
                castY += castDistance;
                break;
        }
        
        this.fishing.castPoint = { x: castX, y: castY };
        
        if (this.fishing.debugGraphics) {
            this.fishing.debugGraphics.destroy();
        }
        this.fishing.debugGraphics = this.add.graphics();
        
        // Draw debug visuals
        if (this.debugMode){
            this.fishing.debugGraphics.lineStyle(2, 0xff0000);
            this.fishing.debugGraphics.strokeCircle(castX, castY, 5);
            this.fishing.debugGraphics.strokeCircle(castX, castY, 2);
            
            this.fishing.debugGraphics.lineStyle(1, 0xff0000);
            this.fishing.debugGraphics.beginPath();
            this.fishing.debugGraphics.moveTo(castX - 8, castY);
            this.fishing.debugGraphics.lineTo(castX + 8, castY);
            this.fishing.debugGraphics.moveTo(castX, castY - 8);
            this.fishing.debugGraphics.lineTo(castX, castY + 8);
            this.fishing.debugGraphics.strokePath();
        }

        this.drawFishingLine();

        const isValid = this.isValidFishingSpot(castX, castY);
        if (isValid) {
            if (this.sounds.splash) {
                this.sounds.splash.stop();
                this.sounds.splash.play();
            }
            this.startFishing();
        } else {
            this.cancelFishing();
        }
    }

    drawFishingLine() {
        if (!this.fishing.castPoint) return;
    
        // Calculate rod tip position
        const rodTipOffsetX = this.player.flipX ? -20 : 22;
        const rodTipOffsetY = 18;
        
        const startX = this.fishing.rod.x + rodTipOffsetX;
        const startY = this.fishing.rod.y + rodTipOffsetY;
    
        // Draw the line
        this.fishing.line.clear();
        this.fishing.line.lineStyle(1, 0xFFFFFF);
        this.fishing.line.beginPath();
        this.fishing.line.moveTo(startX, startY);
        this.fishing.line.lineTo(this.fishing.castPoint.x, this.fishing.castPoint.y);
        this.fishing.line.strokePath();
        
        if (this.debugMode) {
            this.fishing.line.lineStyle(1, 0xFF0000);
            this.fishing.line.strokeCircle(startX, startY, 2);
        }
    }

    isValidFishingSpot(x, y) {
        // Create a temporary physics body at the cast point
        const tempBody = this.add.rectangle(x, y, 2, 2);
        this.physics.add.existing(tempBody);
    
        // Add debug visual for fishing zone check
        if (this.debugMode) {
            const debugGraphics = this.add.graphics();
            debugGraphics.lineStyle(1, 0x00ff00);  // Green outline for fishing check area
            debugGraphics.strokeRect(tempBody.x - 1, tempBody.y - 1, 2, 2);
            
            // Clean up debug graphics after a short delay
            this.time.delayedCall(100, () => {
                if (debugGraphics) debugGraphics.destroy();
            });
        }
    
        // Check if the cast point overlaps with collision bodies (water areas)
        // Collision areas represent water/river that blocks player movement = VALID fishing zones
        const overlapping = this.physics.world.overlap(tempBody, this.collisionBodies);
        
        // Cleanup temp body
        tempBody.destroy();
        
        return overlapping;
    }

    startFishing() {
        this.fishing.isFishing = true;
        this.player.setMovementLocked(true);

        // Create and store the bobber in the fishing object
        this.fishing.bobber = this.add.sprite(this.fishing.castPoint.x, this.fishing.castPoint.y, 'bobberFloating');
        this.fishing.bobber.setDepth(1);
        this.fishing.bobber.play('bobberFloating');

        const biteTime = Phaser.Math.Between(3000, 7000);

        // Generate random time until bite (between 5 and 15 seconds)
        // const biteTime = Phaser.Math.Between(5000, 15000);
        
        
        // Set up the bite timer
        this.fishing.biteTimer = this.time.delayedCall(biteTime, () => {
            if (this.fishing.isFishing) {  // Only trigger bite if still fishing
                this.handleBite();
            }
        }, [], this);
    }

    ///////////////////////////////////////////////////////////////////////////////////////////////////
    //////////////////////////////// MEANINGFUL FISHING GAME CODE HERE ////////////////////////////////
    ///////////////////////////////////////////////////////////////////////////////////////////////////
    startMinigame() {
        this.fishing.currentCatch = itemManager.selectRandomItem(this.fishingCriteria);
        const volatility = itemManager.calculateFishChallengeRating(this.fishing.currentCatch);

        // Center position for our UI elements (viewport coordinates since we'll use scrollFactor 0)
        const centerX = this.cameras.main.width / 2;
        const topY = 0; // Top of viewport
        
        this.gaugeFrame = this.add.sprite(centerX, topY + 24, 'gaugeFrame', 0);
        this.gaugeFrame.setDepth(1000); // High depth to stay above world elements
        this.gaugeFrame.setScrollFactor(0); // Stay fixed to camera view

        // Create the gauge fill sprite at the center
        this.gaugeFill = this.add.sprite(centerX, topY + 24, 'gaugeFill');
        this.gaugeFill.setDepth(1001); // Above gauge frame
        this.gaugeFill.setScrollFactor(0); // Stay fixed to camera view

        // Create the mask container starting from the left edge of the gauge
        this.maskContainer = this.add.graphics();
        // Position at the left edge of where the gauge should start (viewport coordinates)
        this.maskContainer.setPosition(centerX - 57, topY + 24 - 4.5); // 57 is half of 114 (gauge width)
        this.maskContainer.setDepth(1002);
        this.maskContainer.setScrollFactor(0); // Stay fixed to camera view
        
        // Create the initial mask
        this.updateGaugeMask(10);

        // Create and apply the mask
        const mask = this.maskContainer.createGeometryMask();
        this.gaugeFill.setMask(mask);

        this.minigameFrame = this.add.sprite(centerX, topY + 10, 'minigameFrame', 0);
        this.minigameFrame.setDepth(1000);
        this.minigameFrame.setScrollFactor(0); // Stay fixed to camera view
    
        // Set up initial positions (viewport coordinates)
        const startX = centerX - 44;
        this.minigameFish = this.add.sprite(startX, topY + 9, 'minigameFish', 0);
        this.minigameFish.setDepth(1003);
        this.minigameFish.setScrollFactor(0); // Stay fixed to camera view
    
        this.minigameOverlap = this.add.sprite(startX - 44, topY + 9, 'fishOverlapGreen');
        this.minigameOverlap.setDepth(1002);
        this.minigameOverlap.setScrollFactor(0); // Stay fixed to camera view
    
        // Set up game state with viewport-relative bounds
        this.catchComplete = 10; // Start at 10%
        this.minigame = {
            volatility: volatility,
            fishVelocity: 0.8 * volatility, 
            fishDirection: 1,
            fishPaused: false,
            overlapVelocity: 0,
            lastCheckTime: 0,
            failureTimer: null,
            catchProcessing: false,
            bounds: {
                min: centerX - 44,
                max: centerX + 44
            }
        };
    
        // Set up keyboard controls
        this.minigameKeys = {
            A: this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.A),
            LEFT: this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.LEFT),
            D: this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.D),
            RIGHT: this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.RIGHT)
        };
    
        // Create overlap check timer
        this.overlapTimer = this.time.addEvent({
            delay: 100,
            callback: this.checkOverlap,
            callbackScope: this,
            loop: true
        });
    
        // Create fish movement update
        this.fishMovementTimer = this.time.addEvent({
            delay: 16, // ~60fps
            callback: this.updateFishMovement,
            callbackScope: this,
            loop: true
        });
    
        // Create direction change timer based on volatility
        this.directionChangeTimer = this.time.addEvent({
            delay: 3000 / volatility, // Increased base delay
            callback: () => {
                if (this.minigame) {
                    const changeChance = 15 * this.minigame.volatility; // Reduced chance
                    if (Phaser.Math.Between(1, 100) <= changeChance) {
                        this.minigame.fishDirection *= -1;
                        // Reset to base speed in new direction
                        this.minigame.fishVelocity = 0.8 * this.minigame.volatility * this.minigame.fishDirection;
                    }
                }
            },
            loop: true
        });
        
        // Reduced frequency of pauses
        this.pauseTimer = this.time.addEvent({
            delay: 4000 / volatility, // Increased base delay
            callback: () => {
                if (this.minigame && !this.minigame.fishPaused) {
                    const pauseChance = 10 * this.minigame.volatility; // Reduced chance
                    if (Phaser.Math.Between(1, 100) <= pauseChance) {
                        this.minigame.fishPaused = true;
                        this.time.delayedCall(500 / this.minigame.volatility, () => {
                            if (this.minigame) {
                                this.minigame.fishPaused = false;
                                // Reset to base velocity after pause
                                this.minigame.fishVelocity = 0.8 * this.minigame.volatility * this.minigame.fishDirection;
                            }
                        });
                    }
                }
            },
            loop: true
        });
    
        // Play fishing sounds
        const reelingStart = this.sounds.reelingStart;
        const reeling = this.sounds.reeling;
    
        reelingStart.once('complete', () => {
            reeling.play('middle_section');
        });
    
        reelingStart.play();
    }

    updateGaugeMask(percentage) {
        if (!this.maskContainer || !this.gaugeFill) return;
        
        this.maskContainer.clear();
        
        const width = (percentage / 100) * 114;
        
        // Create mask shape without visible fill
        this.maskContainer.beginPath();
        this.maskContainer.fillStyle(0xffffff, 0);  // Alpha 0 makes it invisible
        this.maskContainer.fillRect(0, 0, width, 9);
        
        // Optional: if still seeing the mask, try setting the alpha of the container itself
        this.maskContainer.alpha = 0;
    }
    
    updateFishMovement() {
        if (!this.minigame || !this.minigameFish) return;
    
        // Skip movement if paused
        if (this.minigame.fishPaused) return;
    
        const volatility = this.minigame.volatility;
        
        // Reduced base speed and max velocity scaling
        const baseSpeed = 0.8 * volatility;
        const maxVelocity = 1.5 * volatility;
        
        // Reduced jitter scaling
        const jitter = (Math.random() - 0.5) * (volatility * 0.3);
        
        // Update velocity with reduced jitter
        this.minigame.fishVelocity = (baseSpeed + jitter) * this.minigame.fishDirection;
        
        // Clamp velocity
        this.minigame.fishVelocity = Phaser.Math.Clamp(
            this.minigame.fishVelocity,
            -maxVelocity,
            maxVelocity
        );
    
        // Apply movement within bounds
        const newX = this.minigameFish.x + this.minigame.fishVelocity;
        if (newX >= this.minigame.bounds.min && newX <= this.minigame.bounds.max) {
            this.minigameFish.x = newX;
        } else {
            // Bounce with reduced speed
            this.minigame.fishDirection *= -1;
            this.minigame.fishVelocity = baseSpeed * this.minigame.fishDirection;
        }
    
        // Update overlap movement based on input
        this.updateOverlapMovement();
    }
    
    updateOverlapMovement() {
        if (!this.minigame || !this.minigameOverlap) return;
    
        const acceleration = 0.5;
        const maxVelocity = 6;
        const drag = 0.95;
    
        // Apply input forces
        if (this.minigameKeys.A.isDown || this.minigameKeys.LEFT.isDown) {
            this.minigame.overlapVelocity -= acceleration;
        }
        if (this.minigameKeys.D.isDown || this.minigameKeys.RIGHT.isDown) {
            this.minigame.overlapVelocity += acceleration;
        }
    
        // Apply drag
        this.minigame.overlapVelocity *= drag;
    
        // Clamp velocity
        this.minigame.overlapVelocity = Phaser.Math.Clamp(
            this.minigame.overlapVelocity, 
            -maxVelocity, 
            maxVelocity
        );
    
        // Update position within bounds
        const newX = this.minigameOverlap.x + this.minigame.overlapVelocity;
        this.minigameOverlap.x = Phaser.Math.Clamp(
            newX,
            this.minigame.bounds.min,
            this.minigame.bounds.max
        );
    }
    
    checkOverlap() {
        if (!this.minigame || !this.minigameFish || !this.minigameOverlap) return;
    
        const fishBounds = this.minigameFish.getBounds();
        const overlapBounds = this.minigameOverlap.getBounds();
        const isOverlapping = Phaser.Geom.Rectangle.Overlaps(fishBounds, overlapBounds);
        
        // Check if centers are closely aligned (within 2 pixels)
        const centerAlignment = Math.abs(this.minigameFish.x - this.minigameOverlap.x);
        const perfectOverlap = centerAlignment <= 2;
    
        // Update catch progress
        if (isOverlapping) {
            this.catchComplete += perfectOverlap ? 2 : 1;
        } else {
            this.catchComplete -= 1.2;
        }
    
        // Clamp catch progress
        this.catchComplete = Phaser.Math.Clamp(this.catchComplete, 0, 100);
    
        // Update gauge fill frame
        this.updateGaugeMask(this.catchComplete);
    
        // Check win/loss conditions
        if (this.catchComplete >= 100 && !this.minigame.catchProcessing) {
            this.minigame.catchProcessing = true;  // Set flag to prevent multiple calls
            this.fishCaught();
        } else if (this.catchComplete <= 0) {
            if (!this.minigame.failureTimer) {
                this.minigame.failureTimer = this.time.delayedCall(1000, this.fishLost, [], this);
            }
        } else if (this.minigame.failureTimer) {
            this.minigame.failureTimer.remove();
            this.minigame.failureTimer = null;
        }
    }
    
    async fishCaught() {
        const caughtFish = this.fishing.currentCatch;
        if (!caughtFish) return;
    
        try {
            // Get user data from game registry
            const gameStore = this.game.registry.get('gameStore');
            const { userId } = gameStore;
    
            if (!userId) {
                console.error('No user ID found in game registry');
                return;
            }
    
                        // Get the inventory manager instance
            const inventoryManager = getInventoryManager();
            
            // Add item to inventory using new category system
            // categoryCode 0 = Consumable (as defined in items.json metadata)
            await inventoryManager.addItem({
                itemCode: caughtFish.id,
                categoryCode: 0,
                quantity: 1
            });

            inventoryManager.events.emit('inventory-updated');

            // Play catch sounds and animation
            this.fishCaughtSounds(caughtFish);
            this.player.playColorAnimation('happy5', true);
            
            // Record fishing interaction for care tracking
            careManager.recordInteraction();
            
            // Display catch results with icon
            this.displayCatchResults(caughtFish);
    
            if (this.debugMode) {
                console.log(`Caught a ${caughtFish.rarity} ${caughtFish.name}!`);
                console.log('Added to inventory with itemCode:', caughtFish.id, 'categoryCode:', 0);
            }
    
        } catch (error) {
            console.error('Error processing caught fish:', error);
        } finally {
            this.endFishing();
            // Don't call cleanup() here - it destroys physics groups still in use
            // cleanup() should only be called when the scene is being destroyed
        }
    }
    
    fishCaughtSounds(caughtFish) {
        if (this.sounds.reeling) {
            this.sounds.reeling.stop();
        }
        
        const catchSplash = this.sounds.catchSplash;
        
        // Play splash sound first
        catchSplash.play();
    
        // Play appropriate rarity sound after splash finishes
        catchSplash.once('complete', () => {
            switch (caughtFish.rarity) {
                case 'Legendary':
                    this.sounds.caught4.play();
                    break;
                case 'Epic':
                case 'Rare':
                    this.sounds.caught3.play();
                    break;
                case 'Uncommon':
                    this.sounds.caught2.play();
                    break;
                case 'Normal':
                case 'Junk':
                default:
                    this.sounds.caught1.play();
                    break;
            }
        });
    }
    
    fishLost() {
        // Handle failed catch
        this.sounds.lost.play();
        this.endFishing();
        // Don't call cleanup() here - it destroys physics groups still in use
        // cleanup() should only be called when the scene is being destroyed
    }

    displayCatchResults(fish) {
        // Create a panel to display the catch
        const centerX = this.cameras.main.width / 2;
        const centerY = this.cameras.main.height / 2;
    
        // Create a darkened background panel
        const panel = this.add.rectangle(centerX, centerY, 160, 100, 0x000000)
            .setAlpha(0.8)
            .setDepth(100)
            .setScrollFactor(0); // Stay fixed to camera view
    
        // Get the composed item sprite from ItemManager
        const { sprite: itemSprite } = itemManager.createItemSprite(this, `Consumable_${fish.id}`, centerX, centerY - 20);
        if (itemSprite) {
            itemSprite.setDepth(101);
            itemSprite.setScrollFactor(0); // Stay fixed to camera view
        }
    
        // Add catch text with rarity-colored name
        const nameText = this.add.text(centerX, centerY + 15, fish.name, {
            fontSize: '16px',
            fill: this.getRarityColor(fish.rarity),
            align: 'center'
        })
            .setOrigin(0.5)
            .setDepth(101)
            .setScrollFactor(0); // Stay fixed to camera view
    
        // Add value text
        const valueText = this.add.text(centerX, centerY + 35, `Value: ${fish.value}g`, {
            fontSize: '12px',
            fill: '#ffffff',
            align: 'center'
        })
            .setOrigin(0.5)
            .setDepth(101)
            .setScrollFactor(0); // Stay fixed to camera view
    
        // Optional: Add particle effects for rare+ fish
        if (['Rare', 'Epic', 'Legendary'].includes(fish.rarity)) {
            this.addCatchParticles(centerX, centerY - 20, fish.rarity);
        }
    
        // Add to inventory if available
        if (window.inventoryManager) {
            window.inventoryManager.addItem({
                itemCode: fish.id,
                categoryCode: 0,
                quantity: 1
            });
        }
    
        // Fade out everything after 2 seconds
        this.time.delayedCall(2000, () => {
            this.tweens.add({
                targets: [panel, itemSprite, nameText, valueText],
                alpha: 0,
                duration: 500,
                onComplete: () => {
                    panel.destroy();
                    if (itemSprite) itemSprite.destroy();
                    nameText.destroy();
                    valueText.destroy();
                }
            });
        });
    }

    addCatchParticles(x, y, rarity) {
        const colors = {
            'Rare': 0x0070dd,
            'Epic': 0xa335ee,
            'Legendary': 0xff8000
        };
    
        const particles = this.add.particles(x, y, {
            speed: { min: 50, max: 150 },
            angle: { min: 0, max: 360 },
            scale: { start: 0.5, end: 0 },
            blendMode: 'ADD',
            lifespan: 1000,
            gravityY: 100,
            tint: colors[rarity],
            quantity: 20
        });
        
        particles.setScrollFactor(0); // Stay fixed to camera view
    
        this.time.delayedCall(1000, () => {
            particles.destroy();
        });
    }
    
    getRarityColor(rarity) {
        const colors = {
            'Junk': '#9d9d9d',
            'Normal': '#ffffff',
            'Uncommon': '#1eff00',
            'Rare': '#0070dd',
            'Epic': '#a335ee',
            'Legendary': '#ff8000',
            'Quest': '#ffff00'
        };
        return colors[rarity] || '#ffffff';
    }
    

    endFishing() {
        // Clean up all fishing-related visuals and states
        this.fishing.rod.setVisible(false);
        this.fishing.line.clear();

        if (this.sounds.reelingStart) {
            this.sounds.reelingStart.stop();
        }
        if (this.sounds.reeling) {
            this.sounds.reeling.stop();
        }

        if (this.fishing.debugGraphics) {
            this.fishing.debugGraphics.destroy();
        }
        
        // Destroy the bobber if it exists
        if (this.fishing.bobber) {
            this.fishing.bobber.destroy();
        }

        // Clear timers if they exist
        if (this.fishing.biteTimer) {
            this.fishing.biteTimer.remove();
            this.fishing.biteTimer = null;
        }
        if (this.fishing.hookTimer) {
            this.fishing.hookTimer.remove();
            this.fishing.hookTimer = null;
        }

        // Clean up minigame UI elements
        if (this.gaugeFrame) {
            this.gaugeFrame.destroy();
            this.gaugeFrame = null;
        }
        if (this.gaugeFill) {
            this.gaugeFill.destroy();
            this.gaugeFill = null;
        }
        if (this.maskContainer) {
            this.maskContainer.destroy();
            this.maskContainer = null;
        }
        if (this.minigameFrame) {
            this.minigameFrame.destroy();
            this.minigameFrame = null;
        }
        if (this.minigameFish) {
            this.minigameFish.destroy();
            this.minigameFish = null;
        }
        if (this.minigameOverlap) {
            this.minigameOverlap.destroy();
            this.minigameOverlap = null;
        }

        // Clear minigame timers
        if (this.overlapTimer) {
            this.overlapTimer.remove();
            this.overlapTimer = null;
        }
        if (this.fishMovementTimer) {
            this.fishMovementTimer.remove();
            this.fishMovementTimer = null;
        }

        // Reset fishing state
        this.fishing.isFishing = false;
        this.fishing.hasBite = false;
        this.fishing.hooked = false;
        this.fishing.castPoint = null;
        
        // Reset minigame state
        this.minigame = null;
        
        this.time.delayedCall(1000, () => {
            // Use playColorAnimation which exists instead of play() which might not work with missing animations
            this.player.playColorAnimation("idle2", true);
            this.player.setMovementLocked(false);
        }, [], this);
    }

    cancelFishing() {
        // Only clear if we're not at a valid fishing spot
        if (!this.fishing.castPoint || !this.isValidFishingSpot(this.fishing.castPoint.x, this.fishing.castPoint.y)) {
            this.endFishing();
        }
    }

    createRipples() {
        const maxAttempts = 50;
        const numRipples = 3;
        const ripples = [];
        const rippleWidth = 16;
        const rippleHeight = 6;
        
        let attempts = 0;
        while (ripples.length < numRipples && attempts < maxAttempts) {
            const x = Phaser.Math.Between(rippleWidth/2, this.cameras.main.width - rippleWidth/2);
            const y = Phaser.Math.Between(rippleHeight/2, this.cameras.main.height - rippleHeight/2);
            
            if (this.isValidRippleBounds(x, y, rippleWidth, rippleHeight)) {
                const tooClose = ripples.some(ripple => {
                    return Phaser.Math.Distance.Between(x, y, ripple.x, ripple.y) < rippleWidth;
                });
                
                if (!tooClose) {
                    const ripple = this.add.sprite(x, y, 'ripple', 0);
                    ripple.setDepth(1);
                    ripple.setDisplaySize(rippleWidth, rippleHeight);
                    
                    let currentFrame = 0;
                    const frameCount = 6;
                    
                    this.time.addEvent({
                        delay: 125,
                        callback: () => {
                            if (ripple && ripple.active) {
                                currentFrame = (currentFrame + 1) % frameCount;
                                ripple.setFrame(currentFrame);
                            }
                        },
                        loop: true
                    });
                    
                    ripples.push(ripple);
                }
            }
            attempts++;
        }
        
        this.activeRipples = ripples;
    }

    isValidRippleBounds(centerX, centerY, width, height) {
        const checkPoints = [
            { x: centerX - width/2, y: centerY - height/2 }, // Top-left
            { x: centerX + width/2, y: centerY - height/2 }, // Top-right
            { x: centerX - width/2, y: centerY + height/2 }, // Bottom-left
            { x: centerX + width/2, y: centerY + height/2 }, // Bottom-right
            { x: centerX, y: centerY - height/2 }, // Top middle
            { x: centerX, y: centerY + height/2 }, // Bottom middle
            { x: centerX - width/2, y: centerY }, // Left middle
            { x: centerX + width/2, y: centerY }, // Right middle
            { x: centerX, y: centerY } // Center
        ];
        
        // All check points must be in valid fishing areas (collision bodies = water areas)
        for (const point of checkPoints) {
            if (!this.isValidFishingSpot(point.x, point.y)) {
                return false; // If any point is not in water, ripple placement is invalid
            }
        }
        
        return true; // All points are in water areas
    }

    update() {
        if (this.player && this.player.update) {
            this.player.update();
            
            // Check for player movement to dismiss mood dialogues
            this.checkPlayerMovementForMoodDialogue();
        }
    }
    
    checkPlayerMovementForMoodDialogue() {
        if (!this.player || !this.activeMoodDialogue) {
            return;
        }
        
        const currentPos = { x: this.player.x, y: this.player.y };
        const threshold = 2; // Minimum distance to consider "movement"
        
        const distanceMoved = Phaser.Math.Distance.Between(
            this.lastPlayerPosition.x, 
            this.lastPlayerPosition.y, 
            currentPos.x, 
            currentPos.y
        );
        
        if (distanceMoved > threshold) {
            // Player has moved, dismiss mood dialogue
            this.activeMoodDialogue.destroy();
            this.activeMoodDialogue = null;
        }
        
        // Update last position
        this.lastPlayerPosition.x = currentPos.x;
        this.lastPlayerPosition.y = currentPos.y;
    }

    cleanup() {
        // Clean up fishing-related resources
        if (this.fishing.rod) {
            this.fishing.rod.setVisible(false);
        }
        if (this.fishing.line) {
            this.fishing.line.clear();
        }
        if (this.fishing.bobber) {
            this.fishing.bobber.destroy();
        }
        if (this.fishing.biteTimer) {
            this.fishing.biteTimer.remove();
            this.fishing.biteTimer = null;
        }
        if (this.fishing.hookTimer) {
            this.fishing.hookTimer.remove();
            this.fishing.hookTimer = null;
        }
        
        // Clean up minigame resources
        if (this.overlapTimer) {
            this.overlapTimer.remove();
            this.overlapTimer = null;
        }
        if (this.fishMovementTimer) {
            this.fishMovementTimer.remove();
            this.fishMovementTimer = null;
        }
        
        // Clean up collision bodies
        if (this.collisionBodies) {
            this.collisionBodies.destroy();
        }
        
        // Clean up scene transition bodies
        if (this.sceneTransitionBodies) {
            this.sceneTransitionBodies.destroy();
        }
        
        // Clean up camera
        if (this.cameras.main) {
            this.cameras.main.stopFollow();
        }
        
        // Fishing zones use collision bodies, so no separate cleanup needed
    }
}

/* END OF COMPILED CODE */